'use client';

import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, FileText, Home, Store, Map, User } from 'lucide-react';
import Link from 'next/link';
import { searchSite, SearchResult } from '@/lib/actions/search';


// Simple Debounce hook implementation inside if strictly needed, but let's check
// actually I'll just implement logical debounce inside the effect to be safe.

export function GlobalSearch() {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setIsOpen((open) => !open);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        }
        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            // Tiny timeout to allow mount
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Search Effect
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (query.length >= 2) {
                setLoading(true);
                try {
                    const data = await searchSite(query);
                    setResults(data);
                } catch (error) {
                    console.error('Search error', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setResults([]);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [query]);

    const getIcon = (type: string) => {
        switch (type) {
            case 'actualite': return <FileText className="w-4 h-4" />;
            case 'immobilier': return <Home className="w-4 h-4" />;
            case 'annuaire': return <Store className="w-4 h-4" />;
            case 'quartier': return <Map className="w-4 h-4" />;
            case 'equipe': return <User className="w-4 h-4" />;
            default: return <Search className="w-4 h-4" />;
        }
    }

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 p-2 text-gray-400 hover:text-white transition-colors"
                title="Rechercher (Ctrl+K)"
            >
                <Search className="w-5 h-5" />
                <span className="hidden lg:inline text-sm font-medium opacity-50">Rechercher...</span>
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm p-4 pt-[20vh] flex justify-center items-start animate-in fade-in duration-200">
            {/* Backdrop click to close */}
            <div className="absolute inset-0" onClick={() => setIsOpen(false)} />

            <div className="relative w-full max-w-2xl bg-[#0f1012] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[60vh]">

                {/* Search Input Header */}
                <div className="flex items-center p-4 border-b border-white/10 gap-3">
                    <Search className="w-5 h-5 text-gray-400" />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Rechercher sur tout le site..."
                        className="flex-1 bg-transparent text-lg text-white placeholder:text-gray-600 focus:outline-none"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {loading ? (
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    ) : (
                        <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded">
                            <span className="sr-only">Fermer</span>
                            <kbd className="hidden sm:inline-block text-xs text-gray-500 border border-gray-700 rounded px-1.5 py-0.5">ESC</kbd>
                            <X className="w-5 h-5 sm:hidden text-gray-500" />
                        </button>
                    )}
                </div>

                {/* Results List */}
                <div className="overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
                    {query.length < 2 && (
                        <div className="p-8 text-center text-gray-500 text-sm">
                            Tapez au moins 2 caractères pour chercher...
                        </div>
                    )}

                    {query.length >= 2 && !loading && results.length === 0 && (
                        <div className="p-8 text-center text-gray-500 text-sm">
                            Aucun résultat trouvé pour "{query}".
                        </div>
                    )}

                    {results.length > 0 && (
                        <div className="space-y-1">
                            {results.map((result, idx) => (
                                <Link
                                    key={idx}
                                    href={result.url}
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-start gap-4 p-3 rounded-xl hover:bg-white/5 group transition-colors"
                                >
                                    {result.image_url ? (
                                        <img
                                            src={result.image_url}
                                            alt=""
                                            className="w-12 h-12 rounded-lg object-cover bg-gray-800"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center text-gray-400 group-hover:text-white transition-colors">
                                            {getIcon(result.type)}
                                        </div>
                                    )}

                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-white font-medium truncate group-hover:text-primary transition-colors">
                                            {result.title}
                                        </h4>
                                        <p className="text-sm text-gray-500 truncate">
                                            <span className="uppercase text-[10px] font-bold tracking-wider mr-2 border border-white/10 px-1.5 py-0.5 rounded text-gray-400">
                                                {result.type}
                                            </span>
                                            {result.description}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer Hint */}
                {results.length > 0 && (
                    <div className="bg-white/5 p-2 text-center text-[10px] text-gray-500 border-t border-white/5">
                        {results.length} résultat(s) trouvé(s)
                    </div>
                )}
            </div>
        </div>
    );
}
